import {
    ExtensionLimitSettingsQuery,
    ExtensionLimitSettingsQueryResult,
    useExtensionLimitSettingsQuery,
    useExtensionNumberIdQuery,
} from "./queries.generated";
import {useCreateExtensionSuperuserQuery} from "../create/queries.generated";
import {CapabilitySet, MutationUpdateUserLimitArgs} from "@/api";
import {CallQueueForExtension} from "../tools";
import {BASE_CAPABILITY_SET_NAME} from "@/constants";
import {useCallback} from "react";
import {
    useClientDevicesQuery,
    useLinkedDevicesQuery,
    useProvisionedDevicesQuery,
    useThirdPartyDevicesQuery,
} from "../devices/queries.generated";
import {DevicesUsedInfo} from "../devices/utils";
import {ApolloQueryResult} from "@apollo/client";

type DTSInfoUserAllocated = Record<string, number>;
type DTSInfoDomain = Record<string, {used: number; allocated: number}>;
type DTSInfoSystemLimit = Record<string, {totalAllocated: number; limit: number}>;

export type DeviceTypeSetsInfo = {
    userAllocated: DTSInfoUserAllocated;
    domain: DTSInfoDomain;
    systemLimit?: DTSInfoSystemLimit;
};

/** DataSource for ExtensionLimitSettings */
export type DataSource = {
    // base informations
    limitSettings: ExtensionLimitSettingsQueryResult["data"];
    superuserInformation: {
        totalCapabilityUsage: Record<string, number>;
        totalDeviceUsage: Record<string, number>;
    };

    // derived informations (and helper functions)
    extensionCallQueues: CallQueueForExtension[];
    capabilitySets: CapabilitySet[];
    baseCapabilitySet: CapabilitySet;
    capabilitySetsInUserPackage: (packageName: string) => CapabilitySet[];
    capabilitiesInCapabilitySet: (name: string) => string[];

    deviceTypeSetsInfo: DeviceTypeSetsInfo;
    deviceUsage: DevicesUsedInfo;

    // for showing loading indicator
    anyQueryLoading: boolean;

    // the form will refetch all queries, and use the newly created initial data as initial form data
    refetch: () => Promise<ExtensionLimitSettingsQueryResult["data"]>;
};

export const createInitialData = (
    userId: number,
    data: ExtensionLimitSettingsQueryResult["data"],
): MutationUpdateUserLimitArgs => {
    const extensionQuotaInfo = structuredClone(data.userQuotaInfo);

    let userPackageName = data.users.items[0].userPackageName;

    // if the system defines any user packages, and the user has no package currently, set the first package as default
    if (!userPackageName && data.userPackages.length > 0) {
        userPackageName = data.userPackages[0].name;
    }

    const deviceTypeSets: Record<string, number> = {};
    for (const dts of data.domainAllocation.deviceTypeSets) {
        deviceTypeSets[dts.name] = 0;
    }

    for (const allocation of data.userQuotaInfo.deviceTypeSets) {
        deviceTypeSets[allocation.name] = allocation.value;
    }

    return {
        id: userId,
        userPackageName,
        deviceTypeSets: Object.entries(deviceTypeSets).map(([name, limit]) => ({name, value: limit})),
        capabilitySets: extensionQuotaInfo.capabilitySets,
    };
};

function useAllocationQuery(domainId: number, isSuperuser: boolean) {
    return useCreateExtensionSuperuserQuery({
        variables: {
            domainId,
        },
        fetchPolicy: "no-cache",
        skip: !isSuperuser,
    });
}

type DeviceUsageHook = {
    usage: DevicesUsedInfo;
    loading: boolean;
};

function useDeviceUsage(userId: number): DeviceUsageHook {
    const provisioned = useProvisionedDevicesQuery({
        variables: {
            userId,
        },
        fetchPolicy: "no-cache",
    });

    const thirdparty = useThirdPartyDevicesQuery({
        variables: {
            userId,
        },
        fetchPolicy: "no-cache",
    });

    const linked = useLinkedDevicesQuery({
        variables: {
            userId,
        },
        fetchPolicy: "no-cache",
    });

    const client = useClientDevicesQuery({
        variables: {
            userId,
        },
        fetchPolicy: "no-cache",
    });

    const anyLoading = provisioned.loading || thirdparty.loading || linked.loading || client.loading;

    if (anyLoading)
        return {
            usage: {
                provisioned: 0,
                "client-mobile": 0,
                "client-desktop": 0,
                thirdparty: 0,
                linked: 0,
            },
            loading: true,
        };

    return {
        usage: {
            provisioned: provisioned.data.provisionedDevices.length,
            "client-mobile": client.data.clientDevices.filter((d) => d.type === "MOBILE").length,
            "client-desktop": client.data.clientDevices.filter((d) => d.type === "DESKTOP").length,
            thirdparty: thirdparty.data.thirdPartyDevices.length,
            linked: linked.data.linkedDevices.length,
        },
        loading: false,
    };
}

const getExtensionCallQueues = (
    data: ExtensionLimitSettingsQueryResult["data"],
    userId: number,
): CallQueueForExtension[] => {
    return data.groups.items.map((group) => {
        const extensionMember = group.members.find((member) => member.number.extension.id === userId);
        return {
            ...structuredClone(group),
            controllingLine: extensionMember.controllingLine,
        };
    });
};

const getDeviceTypeSetsInfo = (
    data: ExtensionLimitSettingsQueryResult["data"],
    isSuperuser: boolean,
): DeviceTypeSetsInfo => {
    if (!data) return {userAllocated: {}, domain: {}};

    const result: DeviceTypeSetsInfo = {
        userAllocated: {},
        domain: {},
        systemLimit: isSuperuser ? {} : undefined,
    };

    for (const limit of data.userQuotaInfo.deviceTypeSets) {
        result.userAllocated[limit.name] = limit.value;
    }

    for (const dts of data.domainAllocation.deviceTypeSets) {
        result.domain[dts.name] = {
            used: dts.domainUsed ?? 0,
            allocated: dts.domainAllocated ?? 0,
        };
        if (isSuperuser)
            result.systemLimit[dts.name] = {
                totalAllocated: dts.totalAllocated ?? 0,
                limit: dts.systemLimit,
            };
    }

    return result;
};

export function useDataSource(userId: number, domainId: number, isSuperuser: boolean): DataSource {
    const numberIdQuery = useExtensionNumberIdQuery({
        variables: {
            userId,
        },
        fetchPolicy: "no-cache",
    });

    const limitSettingsQuery = useExtensionLimitSettingsQuery({
        variables: {
            userId,
            domainId,
            numberId: numberIdQuery.data?.users.items[0].numberId,
            isSuperuser,
        },
        skip: !numberIdQuery.data, // since we need numberId, do not query until we have it
        fetchPolicy: "no-cache",
    });

    const allocationsQuery = useAllocationQuery(domainId, isSuperuser);

    const deviceUsage = useDeviceUsage(userId);

    const capabilitySetsInUserPackage = useCallback(
        (packageName: string) => {
            return (
                limitSettingsQuery.data.capabilitySets.filter((s) =>
                    limitSettingsQuery.data.userPackages
                        .find((p) => p.name === packageName)
                        ?.capabilitySets.includes(s.name),
                ) ?? []
            );
        },
        [limitSettingsQuery.data],
    );

    const capabilitiesInCapabilitySet = useCallback(
        (name: string) => limitSettingsQuery.data.capabilitySets.find((s) => s.name === name)?.capabilities ?? [],
        [limitSettingsQuery.data],
    );

    const refetch = useCallback(async () => {
        // to get type safety for the first element, we can type the array like this,
        // and Promise.all will skip the nulls
        const promises: [Promise<ApolloQueryResult<ExtensionLimitSettingsQuery>>, any] = [
            limitSettingsQuery.refetch(),
            null,
        ];

        if (isSuperuser) {
            promises[1] = allocationsQuery.refetch();
        }

        const [newData] = await Promise.all(promises);

        // it is enough to just return this data, as the form will use this as the new initial data
        return newData.data;
    }, [limitSettingsQuery, isSuperuser, allocationsQuery]);

    const anyQueryLoading =
        numberIdQuery.loading || limitSettingsQuery.loading || allocationsQuery.loading || deviceUsage.loading;

    if (anyQueryLoading) {
        return {
            limitSettings: undefined,
            superuserInformation: {
                totalCapabilityUsage: {},
                totalDeviceUsage: {},
            },
            extensionCallQueues: [],
            capabilitySets: [],
            baseCapabilitySet: undefined,
            capabilitySetsInUserPackage: () => [],
            capabilitiesInCapabilitySet: () => [],
            deviceTypeSetsInfo: {
                userAllocated: {},
                domain: {},
                systemLimit: {},
            },
            deviceUsage: deviceUsage.usage, // will have default values
            anyQueryLoading,
            refetch: async () => undefined,
        };
    }

    const superuserInformation: {
        totalCapabilityUsage: Record<string, number>;
        totalDeviceUsage: Record<string, number>;
    } = {
        totalCapabilityUsage: {},
        totalDeviceUsage: {},
    };

    if (isSuperuser) {
        allocationsQuery.data.domainAllocation.capabilitySets.forEach((cap) => {
            superuserInformation.totalCapabilityUsage[cap.name] = cap.totalAllocated;
        });
    }

    if (isSuperuser) {
        allocationsQuery.data.domainAllocation.deviceTypeSets.forEach((dev) => {
            superuserInformation.totalDeviceUsage[dev.name] = dev.totalAllocated;
        });
    }

    const capabilitySets = limitSettingsQuery.data.capabilitySets.filter((s) => s.name !== BASE_CAPABILITY_SET_NAME);
    const baseCapabilitySet = limitSettingsQuery.data.capabilitySets.find((s) => s.name === BASE_CAPABILITY_SET_NAME);

    return {
        limitSettings: limitSettingsQuery.data,
        superuserInformation,
        anyQueryLoading,
        extensionCallQueues: getExtensionCallQueues(limitSettingsQuery.data, userId),
        capabilitySets,
        baseCapabilitySet,
        capabilitySetsInUserPackage,
        capabilitiesInCapabilitySet,
        deviceTypeSetsInfo: getDeviceTypeSetsInfo(limitSettingsQuery.data, isSuperuser),
        deviceUsage: deviceUsage.usage,
        refetch,
    };
}
